import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { array, bool, date, number, object, string } from "yup";
import {
  getIsBuild,
  getIsPurchase,
  getIsRefinance,
  getIsRefinanceAndCashOut,
} from "./modal-utils";
import { LookingToContext } from "../../context/LookingToContext";
import { CountyLimitsContext } from "../../context/CountyLimitsContext";
import { DesktopSettingsModal } from "./DesktopSettingsModal";
import { MobileSettingsModal } from "./MobileSettingsModal";
import { useLocation } from "react-router-dom";
import { getDefaultValuesWhenNotSetExplicitly } from "../../util/getDefaultValuesWhenNotSetExplicitly";
import { getDownPaymentMinimum } from "../../util/getDownPaymentMinimum";
import eligibleStateCheck from "../../util/eligibleStateCheck";

export const IrqSettingsModal = ({
  isFastTrack,
  open,
  onClose,
  customFormEdit,
  onFormEdit,
  runQuotesQuery,
  enteredData,
  isValid,
}) => {
  const lookingToDefault = useContext(LookingToContext);
  const { countyLimits } = useContext(CountyLimitsContext);
  const {
    price,
    downPayment,
    street,
    city,
    county,
    countyFips,
    state,
    stateFips,
    zip,
    lookingTo = lookingToDefault,
    isVeteran,
    isUsCitizen,
    isSelfEmployed,
    hasWaivedEscrow,
    propertyType,
    occupancyType,
    creditScore,
    loanOptions,
    plannedClosingDate,
    existingLoanBalance,
    amountOfCashOut,
    disabledVeteran,
    vaFirstTimeUse,
  } = enteredData;

  const location = useLocation();

  const isFirstTimeHomebuyer = localStorage.getItem("firstTimeBuyer");
  const [isLoading, setIsLoading] = useState(false);
  const isRefinance = getIsRefinance(lookingTo);
  const isRefinanceAndCashOut = getIsRefinanceAndCashOut(lookingTo);
  const isPurchase = getIsPurchase(lookingTo);
  const isBuild = getIsBuild(lookingTo);

  useEffect(() => {
    if (typeof hasWaivedEscrow === "undefined") {
      customFormEdit("hasWaivedEscrow", "No");
    }
  }, []);

  const baseValidationSchema = {
    street: string()
      .required("Address is required")
      .test("address-supported", "This address is not supported", function () {
        const { propertyType, state, stateFips, county, zip } = this.parent;

        // Check if property type is null or Commercial
        if (!propertyType || propertyType === "Commercial") {
          return false;
        }

        // Check if state is ineligible or missing required fields
        if (!eligibleStateCheck(state, stateFips) || !county || !zip) {
          return false;
        }

        return true;
      }),
    city: string().required("City is required"),
    county: string().required("County is required"),
    state: string().required("State is required"),
    zip: string().required("Zip is required"),
    propertyType: string().required("Property Type is required"),
    occupancyType: string().required("Occupancy Type is required"),
    isUsCitizen: bool().nullable().required("Citizenship status is required"),
    isVeteran: bool().nullable().required("Veteran status is required"),
    vaFirstTimeUse: bool()
      .nullable()
      .when("isVeteran", {
        is: true,
        then: bool().nullable().required("First time VA loan is required"),
        otherwise: bool().nullable(),
      }),
    disabledVeteran: bool()
      .nullable()
      .when("isVeteran", {
        is: true,
        then: bool().nullable().required("Veteran status is required"),
        otherwise: bool().nullable(),
      }),
    isSelfEmployed: bool().nullable().required("Self-employment is required"),
    hasWaivedEscrow: string().nullable().required("Escrow choice is required"),
    price: number()
      .positive("Must be a positive number")
      .integer("Must be an integer")
      .required("Price is required")
      .transform((value, originalValue) =>
        /^\d+$/.test(originalValue) ? parseInt(originalValue) : NaN
      )
      .typeError("Must be a positive integer"),
    loanOptions: array().min(1).required(),
    creditScore: number()
      .typeError("Provided value is invalid")
      .min(620, "Must be 620 or higher")
      .max(850, "Must be 850 or lower")
      .required(),
    isFirstTimeHomebuyer: bool().required("Choice is required"),
  };

  const purchaseBuildValidationSchema = {
    downPayment: number()
      .positive("Must be a positive number")
      .test(
        "downPayment",
        "You must exceed the minimum down payment required.",
        function (value) {
          if (!value) return false;
          const downPaymentMinimum = getDownPaymentMinimum({
            countyLimits,
            county: this.parent.county,
            state: this.parent.state,
            countyFips: this.parent.countyFips,
            stateFips: this.parent.stateFips,
            loanAmount: this.parent.price - value,
            propertyType: this.parent.propertyType,
            occupancyType: this.parent.occupancyType,
            lookingTo: "Purchase",
            isFirstTimeHomebuyer:
              isFirstTimeHomebuyer === null ? "false" : isFirstTimeHomebuyer,
          });
          const minimum = this.parent.price * (downPaymentMinimum / 100);
          return value >= minimum;
        }
      )
      .required("Down payment is required")
      .transform((value, originalValue) =>
        /^\d+$/.test(originalValue) ? parseInt(originalValue) : NaN
      )
      .typeError("Must be a positive integer"),
    plannedClosingDate: date().required("Rate lock date is required"),
  };

  const loanAmountForCashout = (parent) =>
    parent.existingLoanBalance +
    (typeof parent.amountOfCashOut === "number" ? parent.amountOfCashOut : 0);

  const refinanceValidationSchema = {
    existingLoanBalance: number()
      .positive("Must be a positive number")
      .integer("Must be an integer")
      .test(
        "existingLoanBalance",
        "You cannot exceed the maximum existing loan balance.",
        function (value) {
          if (!value) return false;
          const minDownPaymentPercent = getDownPaymentMinimum({
            countyLimits,
            county: this.parent.county,
            state: this.parent.state,
            countyFips: this.parent.countyFips,
            stateFips: this.parent.stateFips,
            loanAmount:
              lookingTo === "RefinanceAndCashOut"
                ? loanAmountForCashout(this.parent)
                : this.parent.existingLoanBalance,
            propertyType: this.parent.propertyType,
            occupancyType: this.parent.occupancyType,
            lookingTo,
            isFirstTimeHomebuyer:
              isFirstTimeHomebuyer === null ? "false" : isFirstTimeHomebuyer,
          });
          const maxExistingBalance = this.parent.takeCashOut
            ? this.parent.price -
              this.parent.price * (minDownPaymentPercent / 100) -
              this.parent.amountOfCashOut
            : this.parent.price -
              this.parent.price * (minDownPaymentPercent / 100);
          return value <= maxExistingBalance;
        }
      )
      .required("Existing loan balance is required")
      .transform((value, originalValue) =>
        /^\d+$/.test(originalValue) ? parseInt(originalValue) : NaN
      )
      .typeError("Must be a positive integer"),
  };

  const refinanceAndCashOutValidationSchema = {
    ...refinanceValidationSchema,
    amountOfCashOut: number()
      .positive("Must be a positive number")
      .integer("Must be an integer")
      .test(
        "amountOfCashOut",
        "You cannot exceed the maximum cash out amount.",
        function (value) {
          if (!value) return false;
          const minDownPaymentPercent = getDownPaymentMinimum({
            countyLimits,
            county: this.parent.county,
            state: this.parent.state,
            countyFips: this.parent.countyFips,
            stateFips: this.parent.stateFips,
            loanAmount: loanAmountForCashout(this.parent),

            propertyType: this.parent.propertyType,
            occupancyType: this.parent.occupancyType,
            lookingTo,
            isFirstTimeHomebuyer:
              isFirstTimeHomebuyer === null ? "false" : isFirstTimeHomebuyer,
          });
          const maxCashOut =
            this.parent.price -
            this.parent.price * (minDownPaymentPercent / 100) -
            this.parent.existingLoanBalance;
          return value <= maxCashOut;
        }
      )
      .required("Cash out amount is required")
      .transform((value, originalValue) =>
        /^\d+$/.test(originalValue) ? parseInt(originalValue) : NaN
      )
      .typeError("Must be a positive integer"),
  };

  const getValidationSchema = () => {
    if (isRefinance) {
      return object().shape({
        ...baseValidationSchema,
        ...refinanceValidationSchema,
      });
    } else if (isRefinanceAndCashOut) {
      return object().shape({
        ...baseValidationSchema,
        ...refinanceAndCashOutValidationSchema,
      });
    } else {
      return object().shape({
        ...baseValidationSchema,
        ...purchaseBuildValidationSchema,
      });
    }
  };

  const sendAllFieldsToRedux = (values) => {
    customFormEdit("street", values.street);
    customFormEdit("city", values.city);
    customFormEdit("county", values.county);
    customFormEdit("state", values.state);
    customFormEdit("zip", values.zip);
    customFormEdit("propertyType", values.propertyType);
    customFormEdit("occupancyType", values.occupancyType);
    customFormEdit("isUsCitizen", values.isUsCitizen);
    customFormEdit("isVeteran", values.isVeteran);
    customFormEdit("lookAtVaLoans", values.isVeteran);
    if (values.vaFirstTimeUse && values.isVeteran) {
      customFormEdit("vaFirstTimeUse", values.vaFirstTimeUse);
    }
    if (values.disabledVeteran && values.isVeteran) {
      customFormEdit("disabledVeteran", values.disabledVeteran);
    }
    customFormEdit("veteranType", "ActiveDuty");
    customFormEdit("isSelfEmployed", values.isSelfEmployed);
    customFormEdit("hasWaivedEscrow", values.hasWaivedEscrow);
    customFormEdit("price", values.price);
    customFormEdit("creditScore", values.creditScore);
    customFormEdit("loanOptions", values.loanOptions);
    customFormEdit("plannedClosingDate", values.plannedClosingDate);

    if (isRefinance) {
      customFormEdit("amountOfCashOut", "0");
      customFormEdit("takeCashOut", false);
      customFormEdit("existingLoanBalance", values.existingLoanBalance);
    }
    if (isRefinanceAndCashOut) {
      customFormEdit("amountOfCashOut", values.amountOfCashOut);
      customFormEdit("takeCashOut", true);
      customFormEdit("existingLoanBalance", values.existingLoanBalance);
    }
    if (isPurchase || isBuild) {
      customFormEdit("downPayment", values.downPayment);
    } else {
      customFormEdit("downPayment", "0");
    }
    if (values.isFirstTimeHomebuyer) {
      customFormEdit("isFirstTimeHomebuyer", values.isFirstTimeHomebuyer);
      localStorage.setItem("firstTimeBuyer", "true");
    } else {
      customFormEdit("isFirstTimeHomebuyer", values.isFirstTimeHomebuyer);
      localStorage.setItem("firstTimeBuyer", "false");
    }
  };

  const { search } = location;
  const params = new URLSearchParams(search);

  const defaultValues = getDefaultValuesWhenNotSetExplicitly(params);

  const formik = useFormik({
    initialValues: {
      street: street ?? "",
      city: city ?? "",
      county: county ?? "",
      countyFips: countyFips ?? "",
      state: state ?? "",
      stateFips: stateFips ?? "",
      zip: zip ?? "",
      propertyType: propertyType ?? defaultValues.propertyType,
      occupancyType: occupancyType ?? defaultValues.occupancyType,
      isUsCitizen: isUsCitizen ?? defaultValues.isUsCitizen,
      isVeteran: isVeteran ?? defaultValues.isVeteran,
      vaFirstTimeUse: vaFirstTimeUse ?? null,
      disabledVeteran: disabledVeteran ?? null,
      isSelfEmployed: isSelfEmployed ?? defaultValues.isSelfEmployed,
      hasWaivedEscrow: hasWaivedEscrow ?? defaultValues.hasWaivedEscrow,
      amountOfCashOut: amountOfCashOut ?? "",
      price: price ?? defaultValues.price,
      downPayment: downPayment ?? defaultValues.downPayment,
      existingLoanBalance: existingLoanBalance ?? "",
      creditScore: creditScore ?? defaultValues.creditScore,
      loanOptions: loanOptions ?? defaultValues.loanOptions,
      isFirstTimeHomebuyer:
        isRefinance || isRefinanceAndCashOut
          ? false
          : isFirstTimeHomebuyer ?? defaultValues.isFirstTimeBuyer,
      plannedClosingDate:
        plannedClosingDate ?? defaultValues.plannedClosingDate,
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await sendAllFieldsToRedux(values);
        await runQuotesQuery(lookingTo, {
          ...values,
          lookAtVaLoans: values.isVeteran,
        });
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset VA fields when occupancy type is not Primary
  useEffect(() => {
    if (formik.values.occupancyType !== "Primary") {
      formik.setFieldValue("vaFirstTimeUse", false);
      formik.setFieldValue("disabledVeteran", false);
      formik.setFieldValue("isVeteran", false);
      customFormEdit("isVeteran", false);
      customFormEdit("lookAtVaLoans", false);
      customFormEdit("vaFirstTimeUse", false);
      customFormEdit("disabledVeteran", false);
    }
  }, [formik.values.occupancyType]);

  return (
    <>
      <DesktopSettingsModal
        isFastTrack={isFastTrack}
        open={open}
        onClose={onClose}
        onFormEdit={onFormEdit}
        lookingTo={lookingTo}
        formik={formik}
        isRefinance={isRefinance}
        isRefinanceAndCashOut={isRefinanceAndCashOut}
        isPurchase={isPurchase}
        isBuild={isBuild}
        isLoading={isLoading}
        isValid={isValid}
      />
      <MobileSettingsModal
        isFastTrack={isFastTrack}
        open={open}
        onClose={onClose}
        onFormEdit={onFormEdit}
        lookingTo={lookingTo}
        formik={formik}
        isRefinance={isRefinance}
        isRefinanceAndCashOut={isRefinanceAndCashOut}
        isPurchase={isPurchase}
        isBuild={isBuild}
        isLoading={isLoading}
        isValid={isValid}
      />
    </>
  );
};
